import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AgGridReact,
  Button,
  DropdownButton,
  Flex,
  FullscreenTakeover,
  Grid,
  HelpIndicator,
  LoadingIndicator,
  ModalV2,
  Select,
  Tab,
  Tabs,
  Text,
  TextInput,
  toast,
} from "@jsq/ds/core";
import { useBodyScrollLock } from "@jsq/ds/hooks";
import { Colors } from "@jsq/ds/styles";
import {
  GetDataPackageDocument,
  GetDataPackagesDocument,
  GetQueriesDocument,
  useCreateQueryMutation,
  useFinpubQueryDeleteMutation,
  useExecuteQueryMutation,
  useFinpubQueryQuery,
  useFinpubQueryRestoreMutation,
  useFinpubQueryUpdateMutation,
  useGetPreviewEntitiesQuery,
  useGetQueryRuntimeInputsLazyQuery,
} from "~/graphql/supergraphHooks";
import { usePreviewTransactionBatches } from "~/pages/Reporting/ReportTemplateEditor/hooks/usePreviewTransactionBatches";
import { SPACING } from "~/styles/variables";
import { toGlobalId } from "~/utils/graphql-helpers";
import { useQueryRollupLevels } from "../../../shared/levels/useQueryRollupLevels";
import { toAttributeLeaves } from "../../../shared/RulesSection/attributeCatalogue";
import { useAllPrimaryFields } from "../../../shared/RulesSection/useAllPrimaryFields";
import { supportsTransactionTypes } from "../../../shared/TransactionTypesField/supportedTemplateTypes";
import { useBeforeUnloadPrompt } from "../../hooks";
import { AddFieldPickerSlideout } from "./AddFieldPickerSlideout";
import { ArchiveQueryModal } from "./ArchiveQueryModal";
import { ComingSoon } from "./ComingSoon";
import { FieldSettingsPanel } from "./FieldSettingsPanel";
import { applyFieldSettingsToRaw } from "./FieldSettingsPanel/fieldSettingsMapper";
import { FieldsGrid } from "./FieldsGrid";
import {
  assignDisplayVariants,
  toDisplay,
  toQueryFieldInputs,
  toQueryFieldRows,
  type QueryFieldRow,
} from "./fieldsMapper";
import { FootnoteSettingsPanel } from "./FootnoteSettingsPanel";
import { toFootnoteMergeTagSources } from "./FootnoteSettingsPanel/footnoteMergeTags";
import { applyFootnoteSettingsToRaw } from "./FootnoteSettingsPanel/footnoteSettingsMapper";
import { useFootnoteCustomFields } from "./FootnoteSettingsPanel/useFootnoteCustomFields";
import { FootnotesGrid } from "./FootnotesGrid";
import {
  createBlankFootnoteRaw,
  MAX_FOOTNOTES,
  reorderQueryFootnoteRows,
  toFootnoteDisplay,
  toQueryFootnoteInputs,
  toQueryFootnoteRows,
  type QueryFootnoteRow,
} from "./footnotesMapper";
import { QueryCannotBeArchivedModal } from "./QueryCannotBeArchivedModal";
import { QueryInUseWarningModal } from "./QueryInUseWarningModal";
import { QuerySettingsPanel } from "./QuerySettingsPanel";
import { DEFAULT_QUERY_SETTINGS_VALUES } from "./QuerySettingsPanel/constants";
import {
  DEFAULT_SERVER_ONLY_SETTINGS,
  toQuerySettingsInput,
  toQuerySettingsValues,
  type ServerOnlySettings,
} from "./QuerySettingsPanel/settingsMapper";
import { QueryUnsavedChangesModal } from "./QueryUnsavedChangesModal";
import * as styles from "./styles_vanilla.css";
import type { FieldSettingsValues } from "./FieldSettingsPanel/types";
import type { FootnoteSettingsValues } from "./FootnoteSettingsPanel/types";
import type { NewQuerySeed, QuerySettingsValues } from "./QuerySettingsPanel/types";
import type {
  QueryDetailTab,
  QueryFootnote,
  QueryUsageResolution,
  QueryUsageTemplate,
} from "./types";
import type { DataFormatValues } from "../../../shared/DataFormatSection/types";
import type { Formula } from "../FormulasGrid";
import type { TypeHierarchy } from "../HierarchyTreeGrid";
import type {
  QueryFootnoteFieldsFragment,
  SupportedTemplateType,
} from "~/graphql/supergraphTypes";

type QueryDetailTakeoverProps = {
  queryName: string;
  /** Backend id. `null` while routing in pre-resolve, or in `isNewQuery`
   * mode until the first Save creates the row. */
  queryId: number | null;
  /** Arena scope. `null` for system-level queries. */
  arenaId: number | null;
  /** Required for `finpubQueryCreate` in `isNewQuery` mode. */
  dataPackageVersionId: number | null;
  /** When true, Save fires `finpubQueryCreate` and the settings panel
   * buffers values locally until creation. */
  isNewQuery?: boolean;
  onExit: () => void;
  /** Called when the user cancels an edit of an existing query. Parent
   * navigates back to the read-only view route (not the full exit route). */
  onCancelEdit?: () => void;
  /** Called with the persisted query id after Save succeeds (both
   * create and update). Parent navigates to the read-only view route. */
  onQuerySaved?: (queryId: number) => void;
  /** Read-only view when true; defaults to true. */
  isViewMode?: boolean;
  /** Called when the user clicks "Update query" (after the in-use warning
   * if needed). Hidden when omitted. */
  onRequestEdit?: () => void;
  /** Called after the query is successfully archived. Hidden when omitted. */
  onArchive?: () => void;
  /** Called after the query is successfully restored. Hidden when omitted. */
  onRestore?: () => void;
  /** Called after any query mutation that affects package/list aggregates. */
  onQueryMutated?: () => void | Promise<void>;
  /** True when the query is referenced by at least one active template.
   * Gates the archive action (shows the cannot-archive modal). The
   * "Update query" in-use warning keys off `usageTemplates` instead, which
   * also includes archived and denied templates. */
  isInUse?: boolean;
  /** Resolution state of the per-query template usage data. Until it is
   * `"resolved"`, the archive action and the Update button are disabled so
   * neither an archive modal nor the in-use-warning-gated edit flow can open
   * on unknown usage — mirrors the package-level archive menu item, which is
   * disabled until `dataPackageUsage` resolves. `"loading"` and `"error"`
   * render distinct explanatory tooltips on the archive action. */
  usageResolution?: QueryUsageResolution;
  /** Called when the actions menu is opened while `usageResolution` is
   * `"error"`, so the parent can refetch `dataPackageImpact`. */
  onRetryUsageResolution?: () => void;
  /** Templates shown in the in-use warning modal. */
  usageTemplates?: QueryUsageTemplate[];
  /** Hierarchy catalogue fed into the Add Field Picker. Sourced from
   *  the parent `DataPackageSlideout`'s `finpubDataPackage` query —
   *  same data the `FormulaTakeover` already consumes. */
  typeHierarchy?: TypeHierarchy | null;
  /** Formula catalogue fed into the Add Field Picker. */
  formulas?: Formula[];
  /** When true, hides all mutation affordances (actions menu, update button,
   * restore footer) regardless of individual query state. */
  isDataPackageArchived?: boolean;
  /** Seeded from the New query modal for the deferred-create flow. */
  newQuerySeed?: NewQuerySeed | null;
  /** Supported template type chosen in the Create Query modal, carried via the
   * `?templateType=` route param so it survives a refresh of the new-query
   * route (the in-memory `newQuerySeed` does not). Used as a fallback to seed
   * the query settings when `newQuerySeed` is absent. */
  initialSupportedTemplateType?: SupportedTemplateType;
};

const TAB_CONFIG: { id: QueryDetailTab; label: string }[] = [
  { id: "data-fields", label: "Data fields" },
  { id: "footnotes", label: "Footnotes" },
  { id: "custom-message", label: "Custom message" },
];

const QUERY_MUTATION_REFETCH = [
  GetQueriesDocument,
  GetDataPackageDocument,
  GetDataPackagesDocument,
];

type QueryPreviewRow = {
  id: string;
  name: string;
  value: string;
};

type QueryPreviewExecutionRow = Record<string, unknown>;
type QueryPreviewData = QueryPreviewExecutionRow[];

type StringifiablePreviewValue = number | boolean | bigint | symbol;

const STRINGIFIABLE_PREVIEW_VALUE_TYPES = new Set(["number", "boolean", "bigint", "symbol"]);

const isPreviewExecutionRow = (value: unknown): value is QueryPreviewExecutionRow =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const isUnknownArray = (value: unknown): value is unknown[] => Array.isArray(value);

const NAME_NOT_UNIQUE_ERROR = "Name already taken. Enter a unique name.";
const isNameConflictError = (message: string): boolean =>
  /query name .*(?:conflict|already exists)/i.test(message);
export const toQuerySaveErrorMessage = (message: string): string =>
  isNameConflictError(message) ? NAME_NOT_UNIQUE_ERROR : message;

const isStringifiablePreviewValue = (value: unknown): value is StringifiablePreviewValue =>
  STRINGIFIABLE_PREVIEW_VALUE_TYPES.has(typeof value);

const queryPreviewColumnDefs: AgGridReact.AgGridReactProps<QueryPreviewRow>["columnDefs"] = [
  { field: "name", headerName: "Name", flex: 1 },
  { field: "value", headerName: "Value", flex: 1, wrapText: true, autoHeight: true },
];

const queryPreviewGridOptions: AgGridReact.AgGridReactProps<QueryPreviewRow>["gridOptions"] = {
  domLayout: "autoHeight",
  suppressCellFocus: true,
  suppressHorizontalScroll: true,
};

const formatPreviewValue = (value: unknown): string => {
  if (value === null) return "null";
  if (value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  if (typeof value === "string") return value;
  if (isStringifiablePreviewValue(value)) return value.toString();
  return "";
};

const toPreviewData = (data: unknown): QueryPreviewData => {
  const parsedData: unknown[] = (() => {
    if (isUnknownArray(data)) return data;
    if (typeof data !== "string") return [];

    try {
      const parsed: unknown = JSON.parse(data);
      return isUnknownArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  })();

  return parsedData.filter(isPreviewExecutionRow);
};

const toPreviewRows = (data: QueryPreviewData): QueryPreviewRow[] =>
  data.flatMap((row, index) =>
    Object.entries(row).map(([name, value], valueIndex) => ({
      id: `${index}:${valueIndex}:${name}`,
      name,
      value: formatPreviewValue(value),
    })),
  );

type QueryPreviewModalProps = {
  isOpen: boolean;
  isLoading: boolean;
  rows: QueryPreviewRow[] | null;
  requiresGlTransactions: boolean;
  entities: Array<{ id: string; name: string }>;
  isLoadingEntities: boolean;
  selectedEntityIds: string[];
  onEntitiesChange: (value: string | number | string[] | number[]) => void;
  transactions: Array<{ id: string; label: string }>;
  isLoadingTransactions: boolean;
  selectedTransactionIds: string[];
  onTransactionsChange: (value: string | number | string[] | number[]) => void;
  onExecute: () => void;
  onClose: () => void;
};
// This temporary preview will be updated once there is a business-approved design.
const QueryPreviewModal = ({
  isOpen,
  isLoading,
  rows,
  requiresGlTransactions,
  entities,
  isLoadingEntities,
  selectedEntityIds,
  onEntitiesChange,
  transactions,
  isLoadingTransactions,
  selectedTransactionIds,
  onTransactionsChange,
  onExecute,
  onClose,
}: QueryPreviewModalProps) => (
  <ModalV2
    show={isOpen}
    onHide={onClose}
    title="Preview query"
    size="large"
    data-testid="query-preview-modal"
  >
    <ModalV2.Body>
      {isLoading ? (
        <Flex
          alignItems="center"
          justifyContent="center"
          className={styles.queryPreviewLoading}
        >
          <LoadingIndicator />
        </Flex>
      ) : requiresGlTransactions ? (
        <Flex flexDirection="column" gap={SPACING.MEDIUM}>
          <Text variant="secondary-md">
            Select the transactions to use for this query preview.
          </Text>
          <Select
            label="Entity"
            placeholder="Select entities"
            full
            multiple
            disabled={isLoadingEntities}
            value={selectedEntityIds}
            onChange={onEntitiesChange}
          >
            <Select.Section>
              {entities.map((entity) => (
                <Select.Option key={entity.id} value={entity.id}>
                  {entity.name}
                </Select.Option>
              ))}
            </Select.Section>
          </Select>
          <Select
            label="Transactions"
            placeholder="Select transactions"
            full
            multiple
            disabled={isLoadingTransactions || selectedEntityIds.length === 0}
            value={selectedTransactionIds}
            onChange={onTransactionsChange}
          >
            <Select.Section>
              {transactions.map((transaction) => (
                <Select.Option key={transaction.id} value={transaction.id}>
                  {transaction.label}
                </Select.Option>
              ))}
            </Select.Section>
          </Select>
        </Flex>
      ) : rows && rows.length > 0 ? (
        <Grid<QueryPreviewRow>
          className={styles.queryPreviewGrid}
          data-testid="query-preview-grid"
          rowData={rows}
          getRowId={(params) => params.data.id}
          columnDefs={queryPreviewColumnDefs}
          gridOptions={queryPreviewGridOptions}
          suppressColumnVirtualisation
          suppressRowVirtualisation
        />
      ) : (
        <Text variant="secondary-md">No preview data returned.</Text>
      )}
    </ModalV2.Body>
    <ModalV2.Footer>
      <Flex justifyContent="flex-end" gap={SPACING.SMALL}>
        <Button variant="neutral" onClick={onClose} disabled={isLoading}>
          {requiresGlTransactions ? "Cancel" : "Close"}
        </Button>
        {requiresGlTransactions && (
          <Button
            variant="primary"
            onClick={onExecute}
            disabled={selectedTransactionIds.length === 0 || isLoading}
          >
            Preview
          </Button>
        )}
      </Flex>
    </ModalV2.Footer>
  </ModalV2>
);

export const QueryDetailTakeover = ({
  queryName,
  queryId,
  arenaId,
  dataPackageVersionId,
  isNewQuery = false,
  onExit,
  onCancelEdit,
  onQuerySaved,
  isViewMode = true,
  onRequestEdit,
  onArchive,
  onRestore,
  onQueryMutated,
  isInUse = false,
  usageResolution = "resolved",
  onRetryUsageResolution,
  usageTemplates = [],
  typeHierarchy = null,
  formulas = [],
  isDataPackageArchived = false,
  newQuerySeed = null,
  initialSupportedTemplateType,
}: QueryDetailTakeoverProps) => {
  const [searchValue, setSearchValue] = useState("");
  const [footnoteSearchValue, setFootnoteSearchValue] = useState("");
  const [activeTab, setActiveTab] = useState<QueryDetailTab>("data-fields");
  const [isAddFieldOpen, setIsAddFieldOpen] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isCannotArchiveModalOpen, setIsCannotArchiveModalOpen] = useState(false);
  const [isInUseWarningOpen, setIsInUseWarningOpen] = useState(false);
  const [isSettingsPanelOpen, setIsSettingsPanelOpen] = useState(false);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isPreviewingQuery, setIsPreviewingQuery] = useState(false);
  const [previewRows, setPreviewRows] = useState<QueryPreviewRow[] | null>(null);
  const [requiresPreviewGlTransactions, setRequiresPreviewGlTransactions] = useState(false);
  const [selectedPreviewEntityIds, setSelectedPreviewEntityIds] = useState<string[]>([]);
  const [selectedPreviewTransactionIds, setSelectedPreviewTransactionIds] = useState<string[]>(
    [],
  );
  const [queryNameError, setQueryNameError] = useState<string | null>(null);

  // Local mutable copy of server fields. Holding `QueryFieldRow[]` (display
  // + raw) lets Save round-trip un-edited fields verbatim — see fieldsMapper.
  const [fieldRows, setFieldRows] = useState<QueryFieldRow[]>([]);

  // Local mutable copy of server footnotes. Mirrors `fieldRows`: the raw
  // companion lets Save round-trip un-edited footnotes verbatim — see
  // footnotesMapper. Until this slice the takeover never sent `footnotes`, so
  // copy-on-write carried them forward; now we own the write path.
  const [footnoteRows, setFootnoteRows] = useState<QueryFootnoteRow[]>([]);
  // Tracks footnote-level edits (add / edit / delete) so the beforeunload
  // guard only fires on real unsaved changes. Reset after a successful save.
  const [hasUnsavedFootnoteEdits, setHasUnsavedFootnoteEdits] = useState(false);
  // Footnote currently open in the settings panel. `isNew` distinguishes the
  // create flow (append on save) from edit (replace on save). `null` = closed.
  const [editingFootnote, setEditingFootnote] = useState<{
    raw: QueryFootnoteFieldsFragment;
    isNew: boolean;
  } | null>(null);
  // Monotonically-decreasing placeholder ids for unsaved new footnotes; the
  // backend assigns real ids on Save. Kept in a ref so re-renders don't reuse
  // an id that's already attached to a buffered row.
  const newFootnoteIdRef = useRef(-1);

  // Settings buffered while in `isNewQuery` mode; flushed into the
  // `finpubQueryCreate` payload on Save. `null` → use server defaults.
  const [pendingSettings, setPendingSettings] = useState<QuerySettingsValues | null>(null);
  // Field-level edits buffered until top-level Save. Folded into `fieldRows`
  // on save via `applyFieldSettingsToRaw` (`effectiveFieldRows` below) so the
  // existing `toQueryFieldInputs` write path serializes them. Mirrors the
  // `pendingSettings` buffer for query-level settings.
  const [pendingFieldEdits, setPendingFieldEdits] = useState<
    ReadonlyMap<string, FieldSettingsValues>
  >(() => new Map());
  // Tracks field-level edits (deletions, settings buffer) so the beforeunload
  // guard only fires when there are actual unsaved changes. Reset after a
  // successful save.
  const [hasUnsavedFieldEdits, setHasUnsavedFieldEdits] = useState(false);

  // The takeover is position: fixed over the data-structures page, which can
  // overflow and paint document scrollbars that scroll the hidden background.
  useBodyScrollLock({ isLocked: true });

  // The full catalogue (not a page): attribute metadata — rules, type
  // inference, level support — must resolve every column a query can hold.
  const primaryFieldItems = useAllPrimaryFields(arenaId);
  const queryAttributes = useMemo(
    () =>
      primaryFieldItems?.map((field) => ({
        name: field.name,
        logicalName: field.displayName || field.name,
        tableName: field.tableName,
        type: field.type,
        filterable: field.filterable,
        supportedLevels: field.supportedLevels,
      })),
    [primaryFieldItems],
  );
  const attributeLeaves = useMemo(() => toAttributeLeaves(queryAttributes), [queryAttributes]);
  const { options: rollupLevelOptions } = useQueryRollupLevels();
  // GPX custom fields
  const {
    customFieldOptionGroups,
    isLoading: isLoadingCustomFields,
    error: customFieldsError,
  } = useFootnoteCustomFields(arenaId, editingFootnote !== null);
  const rollupLevelOptionsRef = useRef(rollupLevelOptions);
  rollupLevelOptionsRef.current = rollupLevelOptions;

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchValue("");
  }, []);

  const handleFootnoteSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFootnoteSearchValue(event.target.value);
    },
    [],
  );

  const handleClearFootnoteSearch = useCallback(() => {
    setFootnoteSearchValue("");
  }, []);

  const handleOpenSettings = useCallback(() => {
    setQueryNameError(null);
    setIsSettingsPanelOpen(true);
  }, []);

  const handleCloseSettings = useCallback(() => {
    setQueryNameError(null);
    setIsSettingsPanelOpen(false);
  }, []);

  // Skipped in `isNewQuery` mode or pre-resolve; the settings panel falls
  // back to defaults seeded with the parent-provided name.
  const { data: queryData } = useFinpubQueryQuery({
    variables: { queryId: queryId!, arenaId },
    skip: queryId === null,
    fetchPolicy: "cache-and-network",
  });

  const finpubQuery = queryData?.finpubQuery;

  // Backend fields the UI doesn't surface yet (see settingsMapper.ts). Held
  // in a ref so the submit handler can re-attach them without the form
  // knowing they exist.
  const serverOnlyRef = useRef<ServerOnlySettings>(DEFAULT_SERVER_ONLY_SETTINGS);

  // `id === queryId` guards the cache-and-network race — see `seededQueryIdRef` below.
  const mappedSettings = useMemo(
    () =>
      finpubQuery && finpubQuery.id === queryId ? toQuerySettingsValues(finpubQuery) : null,
    [finpubQuery, queryId],
  );

  useEffect(() => {
    if (mappedSettings) {
      serverOnlyRef.current = mappedSettings.serverOnly;
    } else {
      serverOnlyRef.current = DEFAULT_SERVER_ONLY_SETTINGS;
    }
  }, [mappedSettings]);

  const newQueryDefaultSettings = useMemo<QuerySettingsValues | null>(() => {
    if (!isNewQuery || (!newQuerySeed && !initialSupportedTemplateType)) return null;
    return {
      ...DEFAULT_QUERY_SETTINGS_VALUES,
      name: queryName,
      // URL-param template type is a refresh-safe fallback; the in-session
      // seed (which also carries transaction types) wins when present.
      ...(initialSupportedTemplateType && {
        supportedTemplateType: initialSupportedTemplateType,
      }),
      ...newQuerySeed,
    };
  }, [isNewQuery, newQuerySeed, initialSupportedTemplateType, queryName]);

  const settingsInitialValues = useMemo<QuerySettingsValues>(
    () =>
      pendingSettings ??
      mappedSettings?.values ??
      newQueryDefaultSettings ?? { ...DEFAULT_QUERY_SETTINGS_VALUES, name: queryName },
    [mappedSettings, newQueryDefaultSettings, pendingSettings, queryName],
  );

  // `fieldRows` with the buffered field-level edits folded back into `raw`.
  // Driven by `pendingFieldEdits`; the grid + the panel + the save payload
  // all read this so an open-edit-save-reopen cycle stays consistent without
  // mutating server state until Save.
  const effectiveFieldRows = useMemo<QueryFieldRow[]>(
    () =>
      fieldRows.map((row) => {
        const pending = pendingFieldEdits.get(row.display.id);
        const nextRaw = pending
          ? applyFieldSettingsToRaw(row.raw, pending, attributeLeaves)
          : row.raw;
        return {
          display: toDisplay(nextRaw, row.display.variant, rollupLevelOptions),
          raw: nextRaw,
        };
      }),
    [attributeLeaves, fieldRows, pendingFieldEdits, rollupLevelOptions],
  );

  // FORMULA_COMPONENT rows are hidden from the grid — they are managed through
  // the parent FORMULA row's Field Settings panel (one per formula component occurrence).
  // Variants are renumbered here so the column counts only the rows the user
  // can actually see, and stays contiguous after an add / delete / reorder.
  const gridRows = useMemo(
    () =>
      assignDisplayVariants(
        effectiveFieldRows.filter((r) => r.raw.referenceKind !== "FORMULA_COMPONENT"),
      ),
    [effectiveFieldRows],
  );

  const fields = useMemo(() => gridRows.map((row) => row.display), [gridRows]);

  const footnoteFieldOptions = useMemo(() => toFootnoteMergeTagSources(fields), [fields]);

  const selectedFieldRow = gridRows.find((row) => row.display.id === selectedFieldId) ?? null;

  // Display rows for the footnotes grid.
  const footnotes = useMemo<QueryFootnote[]>(
    () => footnoteRows.map((row) => toFootnoteDisplay(row.raw)),
    [footnoteRows],
  );

  // Seed `fieldRows` once per `queryId`. The local state holds in-flight
  // edits, so cache-and-network refetches must not clobber it.
  const seededQueryIdRef = useRef<number | null>(null);
  useEffect(() => {
    // Functional setState keeps the empty-array reference stable so we
    // skip re-rendering the grid when there's nothing to clear.
    const clearFieldRows = () => setFieldRows((prev) => (prev.length === 0 ? prev : []));
    const clearFootnoteRows = () => setFootnoteRows((prev) => (prev.length === 0 ? prev : []));

    if (queryId === null) {
      seededQueryIdRef.current = null;
      clearFieldRows();
      clearFootnoteRows();
      return;
    }
    // Already seeded — refetches must not overwrite local edits.
    if (seededQueryIdRef.current === queryId) return;
    // Different id, payload not in yet — drop the previous query's rows so
    // we don't briefly render them under the new id.
    clearFieldRows();
    clearFootnoteRows();
    if (!finpubQuery) return;
    // Cache-and-network can hand us A's data under B's id while B is in
    // flight; seeding from a mismatch would lock A under B.
    if (finpubQuery.id !== queryId) return;
    seededQueryIdRef.current = queryId;
    setFieldRows(toQueryFieldRows(finpubQuery.fields, rollupLevelOptionsRef.current));
    setFootnoteRows(toQueryFootnoteRows(finpubQuery.footnotes));
  }, [queryId, finpubQuery]);

  const [updateFinpubQuery, { loading: isUpdating }] = useFinpubQueryUpdateMutation();
  const [createFinpubQuery, { loading: isCreating }] = useCreateQueryMutation();
  const [deleteFinpubQuery, { loading: isArchiving }] = useFinpubQueryDeleteMutation();
  const [restoreFinpubQuery, { loading: isRestoring }] = useFinpubQueryRestoreMutation();
  const [executeQuery] = useExecuteQueryMutation();
  const [getQueryRuntimeInputs] = useGetQueryRuntimeInputsLazyQuery({
    fetchPolicy: "network-only",
  });
  const { data: previewEntitiesData, loading: isLoadingPreviewEntities } =
    useGetPreviewEntitiesQuery({
      variables: { arenaId: toGlobalId("ArenaObjectType", arenaId ?? 0) },
      skip: !isPreviewModalOpen || !requiresPreviewGlTransactions || arenaId === null,
      fetchPolicy: "cache-and-network",
    });
  const previewEntities = useMemo(
    () =>
      previewEntitiesData?.investmentEntitiesByArena?.edges
        .map((edge) => edge?.node)
        .filter((entity): entity is NonNullable<typeof entity> => Boolean(entity))
        .map((entity) => ({ id: entity.id, name: entity.name })) ?? [],
    [previewEntitiesData],
  );
  const { batches: previewTransactions, loading: isLoadingPreviewTransactions } =
    usePreviewTransactionBatches(selectedPreviewEntityIds, arenaId, queryId);
  const isSaving = isCreating || isUpdating;
  const canSaveQuery =
    !isSaving &&
    (isNewQuery
      ? dataPackageVersionId !== null
      : queryId !== null && seededQueryIdRef.current === queryId);

  // Deferred-save contract: the panel never mutates on its own. Both
  // create and edit flows buffer here and let the takeover's "Save query"
  // button flush in a single round-trip — that way a user who hits
  // Update settings then Cancel doesn't ship a half-applied change to BE.
  // `async` keeps the panel's `Promise<void>` contract; the await isn't
  // needed today.
  const handleSaveSettings = useCallback(
    // eslint-disable-next-line @typescript-eslint/require-await
    async (values: QuerySettingsValues) => {
      setQueryNameError(null);
      setPendingSettings(values);
      setIsSettingsPanelOpen(false);
    },
    [],
  );

  const handleOpenAddField = useCallback(() => {
    setIsAddFieldOpen(true);
  }, []);

  const handleCloseAddField = useCallback(() => {
    setIsAddFieldOpen(false);
  }, []);

  const handleConfigureField = useCallback((fieldId: string) => {
    setSelectedFieldId(fieldId);
  }, []);

  const handleCloseFieldSettings = useCallback(() => {
    setSelectedFieldId(null);
  }, []);

  const handleSaveFieldSettings = useCallback(
    (fieldId: string, values: FieldSettingsValues): Promise<void> => {
      setPendingFieldEdits((prev) => {
        const next = new Map(prev);
        next.set(fieldId, values);
        return next;
      });

      setHasUnsavedFieldEdits(true);
      setSelectedFieldId(null);
      return Promise.resolve();
    },
    [],
  );

  const handleDeleteField = useCallback((fieldId: string) => {
    setHasUnsavedFieldEdits(true);
    setFieldRows((prev) => prev.filter((row) => row.display.id !== fieldId));
    // Close the side panel if the deleted field was open in it.
    setSelectedFieldId((current) => (current === fieldId ? null : current));
  }, []);

  const handleDeleteFootnote = useCallback((footnoteId: string) => {
    setHasUnsavedFootnoteEdits(true);
    setFootnoteRows((prev) => prev.filter((row) => String(row.raw.id) !== footnoteId));
    // Close the panel if the deleted footnote was open in it.
    setEditingFootnote((current) =>
      current && String(current.raw.id) === footnoteId ? null : current,
    );
  }, []);

  // Reorder buffered rows to match the dropped order (`sortOrder` is re-indexed
  // on Save). Only flag unsaved edits when the order actually changed.
  const handleReorderFootnotes = useCallback(
    (orderedIds: string[]) => {
      const next = reorderQueryFootnoteRows(footnoteRows, orderedIds);
      const unchanged =
        next.length === footnoteRows.length &&
        next.every((row, idx) => row === footnoteRows[idx]);
      if (unchanged) return;
      setFootnoteRows(next);
      setHasUnsavedFootnoteEdits(true);
    },
    [footnoteRows],
  );

  // "New footnote" opens the settings panel seeded with a blank freeform
  // footnote; the row is appended to the buffer only on the panel's Save.
  const handleOpenAddFootnote = useCallback(() => {
    // Guard the count in addition to disabling the button, so a stray call
    // (e.g. keyboard activation) can't exceed the cap.
    if (footnoteRows.length >= MAX_FOOTNOTES) return;
    const id = newFootnoteIdRef.current;
    newFootnoteIdRef.current -= 1;
    setEditingFootnote({ raw: createBlankFootnoteRaw(id), isNew: true });
  }, [footnoteRows.length]);

  const handleEditFootnote = useCallback(
    (footnoteId: string) => {
      const row = footnoteRows.find((r) => String(r.raw.id) === footnoteId);
      if (row) setEditingFootnote({ raw: row.raw, isNew: false });
    },
    [footnoteRows],
  );

  const handleCloseFootnotePanel = useCallback(() => {
    setEditingFootnote(null);
  }, []);

  // Uniqueness is checked against the buffered rows on Save footnote, so a
  // clash is caught in the panel that caused it rather than coming back from
  // the server on Save query. The footnote being edited is excluded so it
  // doesn't collide with its own persisted values.
  const { takenFootnoteNames, takenFootnoteCustomFieldIds } = useMemo(() => {
    const others = footnoteRows.filter((row) => row.raw.id !== editingFootnote?.raw.id);
    return {
      takenFootnoteNames: new Set(others.map((row) => row.raw.name.trim())),
      takenFootnoteCustomFieldIds: new Set(
        others
          .filter((row) => row.raw.customFieldId != null)
          .map((row) => String(row.raw.customFieldId)),
      ),
    };
  }, [footnoteRows, editingFootnote]);

  const handleSaveFootnote = useCallback(
    (values: FootnoteSettingsValues) => {
      if (!editingFootnote) return;
      const updatedRaw = applyFootnoteSettingsToRaw(editingFootnote.raw, values);
      setFootnoteRows((prev) =>
        editingFootnote.isNew
          ? [...prev, { raw: updatedRaw }]
          : prev.map((row) => (row.raw.id === updatedRaw.id ? { raw: updatedRaw } : row)),
      );
      setHasUnsavedFootnoteEdits(true);
      setEditingFootnote(null);
    },
    [editingFootnote],
  );

  // The row arrives with an empty `mergeField`; the backend allocates its
  // tag on save (guaranteed above every suffix it has ever issued for the
  // alias, so a deleted field's tag is never rebound to this one).
  const handleAddField = useCallback((row: QueryFieldRow) => {
    setHasUnsavedFieldEdits(true);
    setFieldRows((prev) => {
      const merged = [...prev, row];
      return merged.map((r, idx) =>
        r.raw.sortOrder === idx ? r : { ...r, raw: { ...r.raw, sortOrder: idx } },
      );
    });
  }, []);

  const handleSave = useCallback(async (): Promise<boolean> => {
    try {
      // New-query mode: persist settings + fields + name in one
      // `finpubQueryCreate`. On success the parent swaps the URL and
      // subsequent saves fall into the update branch below.
      if (isNewQuery) {
        if (!dataPackageVersionId) {
          toast.error("Cannot save: data package is not fully loaded.");
          return false;
        }
        // Prefer the name buffered in the settings panel over the URL-carried one.
        const effectiveName = pendingSettings?.name?.trim() || queryName;
        // Always send settings on create so values like `supportedTemplateType`
        // and `supportedTransactionTypes` (seeded from the Create Query modal)
        // are persisted even when the user never opened the Settings panel.
        // `settingsInitialValues` already incorporates `newQueryDefaultSettings`
        // (seed + `initialSupportedTemplateType`).
        const effectiveSettings = pendingSettings ?? settingsInitialValues;
        const { data, errors } = await createFinpubQuery({
          variables: {
            name: effectiveName,
            dataPackageVersionId,
            arenaId,
            settings: toQuerySettingsInput(effectiveSettings, serverOnlyRef.current),
            fields: toQueryFieldInputs(effectiveFieldRows),
            footnotes: toQueryFootnoteInputs(footnoteRows),
            ...(supportsTransactionTypes(effectiveSettings.supportedTemplateType) && {
              transactionTypeNodeIds: effectiveSettings.supportedTransactionTypes,
            }),
          },
          refetchQueries: QUERY_MUTATION_REFETCH,
          awaitRefetchQueries: true,
        });
        if (errors?.length) {
          throw new Error(errors[0]?.message ?? "Failed to create query");
        }
        const result = data?.finpubQueryCreate;
        if (!result?.success || result.id == null) {
          throw new Error(result?.message || "Failed to create query");
        }
        toast.success("Query saved");
        setPendingSettings(null);
        setPendingFieldEdits(new Map());
        setHasUnsavedFieldEdits(false);
        setHasUnsavedFootnoteEdits(false);
        await onQueryMutated?.();
        onQuerySaved?.(result.id);
        return true;
      }

      if (queryId === null) {
        toast.error("Cannot save: query id is not available.");
        return false;
      }
      if (seededQueryIdRef.current !== queryId) {
        toast.error("Cannot save: query fields are still loading.");
        return false;
      }
      // Single-mutation flush: include `name` / `settings` only when the
      // panel buffered changes. Sending them unconditionally would re-
      // serialize unmodified server state and risk lossy round-trips
      // through `settingsMapper`.
      const { data, errors } = await updateFinpubQuery({
        variables: {
          queryId,
          arenaId,
          fields: toQueryFieldInputs(effectiveFieldRows),
          footnotes: toQueryFootnoteInputs(footnoteRows),
          ...(pendingSettings && {
            name: pendingSettings.name,
            settings: toQuerySettingsInput(pendingSettings, serverOnlyRef.current),
            ...(supportsTransactionTypes(pendingSettings.supportedTemplateType) && {
              transactionTypeNodeIds: pendingSettings.supportedTransactionTypes,
            }),
          }),
        },
        refetchQueries: QUERY_MUTATION_REFETCH,
        awaitRefetchQueries: true,
      });
      if (errors?.length) {
        throw new Error(errors[0]?.message ?? "Failed to save query");
      }
      const result = data?.finpubQueryUpdate;
      if (!result || !result.success) {
        throw new Error(result?.message || "Failed to save query");
      }
      toast.success("Query saved");
      // Re-seed from the response so rows that were just created adopt the
      // merge tags the backend allocated. Keeping the pre-save rows would
      // leave them with an empty tag, and the next update would omit
      // `mergeField` again — allocating a second tag for the same field.
      setFieldRows(
        result.query
          ? toQueryFieldRows(result.query.fields, rollupLevelOptions)
          : effectiveFieldRows,
      );
      if (result.query) {
        setFootnoteRows(toQueryFootnoteRows(result.query.footnotes));
      }
      setPendingSettings(null);
      setPendingFieldEdits(new Map());
      setHasUnsavedFieldEdits(false);
      setHasUnsavedFootnoteEdits(false);
      await onQueryMutated?.();
      onQuerySaved?.(queryId);
      return true;
    } catch (error) {
      const rawMessage =
        error instanceof Error && error.message
          ? error.message
          : "Failed to save query. Please try again.";
      const message = toQuerySaveErrorMessage(rawMessage);
      if (message === NAME_NOT_UNIQUE_ERROR) {
        setQueryNameError(message);
        setIsSettingsPanelOpen(true);
        return false;
      }
      toast.error(message);
      return false;
    }
  }, [
    arenaId,
    createFinpubQuery,
    dataPackageVersionId,
    effectiveFieldRows,
    footnoteRows,
    isNewQuery,
    onQueryMutated,
    onQuerySaved,
    pendingSettings,
    queryId,
    queryName,
    rollupLevelOptions,
    settingsInitialValues,
    updateFinpubQuery,
  ]);

  const handleStartEdit = useCallback(() => {
    // Warn on any referencing template — including archived and denied ones,
    // which don't count as "in use" for archive gating but still matter when
    // updating the query. Falls through to editing when there's nothing to
    // show. Only reachable once usage is resolved (the Update button is
    // disabled until then), so an empty list really means "not referenced"
    // rather than "still loading".
    if (usageTemplates.length > 0) {
      setIsInUseWarningOpen(true);
    } else {
      onRequestEdit?.();
    }
  }, [usageTemplates.length, onRequestEdit]);

  const handleClickArchive = useCallback(() => {
    if (isInUse) {
      setIsCannotArchiveModalOpen(true);
    } else {
      setIsArchiveModalOpen(true);
    }
  }, [isInUse]);

  // Failed usage lookups keep the archive action disabled, so opening the
  // actions menu doubles as the retry affordance (the action's tooltip
  // points the user at it).
  const handleActionsMenuToggle = useCallback(
    (isOpen: boolean) => {
      if (isOpen && usageResolution === "error") {
        onRetryUsageResolution?.();
      }
    },
    [usageResolution, onRetryUsageResolution],
  );

  const executePreviewQuery = useCallback(
    async (glTransactionIds?: string[]) => {
      if (arenaId === null || queryId === null) return;

      setIsPreviewingQuery(true);
      try {
        const { data } = await executeQuery({
          variables: {
            arenaId,
            queryId,
            ...(glTransactionIds && {
              runtimeParameters: JSON.stringify({
                recipient_metadata: { gl_transaction_ids: glTransactionIds },
              }),
            }),
          },
        });
        const result = data?.finpubQueryExecute;
        if (!result || result.data == null) {
          toast.error(result?.message || "Failed to preview query.");
          return;
        }
        setRequiresPreviewGlTransactions(false);
        setPreviewRows(toPreviewRows(toPreviewData(result.data)));
      } catch {
        toast.error("Failed to preview query.");
      } finally {
        setIsPreviewingQuery(false);
      }
    },
    [arenaId, executeQuery, queryId],
  );

  const handlePreviewQuery = useCallback(async () => {
    if (arenaId === null || queryId === null) {
      toast.error("Cannot preview query without an arena and query id.");
      return;
    }

    setIsPreviewModalOpen(true);
    setIsPreviewingQuery(true);
    setPreviewRows(null);
    setRequiresPreviewGlTransactions(false);
    setSelectedPreviewEntityIds([]);
    setSelectedPreviewTransactionIds([]);
    try {
      const { data } = await getQueryRuntimeInputs({
        variables: { arenaId, queryId },
      });
      const tokens = data?.finpubQueryRuntimeInputs?.tokens ?? [];
      const unsupportedTokens = tokens.filter((token) => token !== "gl_transaction_ids");
      if (unsupportedTokens.length > 0) {
        toast.error(
          `Preview does not support runtime input(s): ${unsupportedTokens.join(", ")}.`,
        );
        setIsPreviewModalOpen(false);
        setIsPreviewingQuery(false);
        return;
      }
      if (tokens.includes("gl_transaction_ids")) {
        setRequiresPreviewGlTransactions(true);
        setIsPreviewingQuery(false);
        return;
      }
    } catch {
      toast.error("Failed to load query preview inputs.");
      setIsPreviewModalOpen(false);
      setIsPreviewingQuery(false);
      return;
    }

    await executePreviewQuery();
  }, [arenaId, executePreviewQuery, getQueryRuntimeInputs, queryId]);

  const handlePreviewEntitiesChange = useCallback(
    (value: string | number | string[] | number[]) => {
      setSelectedPreviewEntityIds(Array.isArray(value) ? value.map(String) : [String(value)]);
      setSelectedPreviewTransactionIds([]);
    },
    [],
  );

  const handlePreviewTransactionsChange = useCallback(
    (value: string | number | string[] | number[]) => {
      setSelectedPreviewTransactionIds(
        Array.isArray(value) ? value.map(String) : [String(value)],
      );
    },
    [],
  );

  const handleExecutePreview = useCallback(() => {
    void executePreviewQuery(selectedPreviewTransactionIds);
  }, [executePreviewQuery, selectedPreviewTransactionIds]);

  const handleClosePreviewModal = useCallback(() => {
    setIsPreviewModalOpen(false);
    setRequiresPreviewGlTransactions(false);
  }, []);

  const handleConfirmArchive = useCallback(async () => {
    if (queryId === null) return;
    try {
      const { data, errors } = await deleteFinpubQuery({
        variables: { queryId, arenaId: arenaId ?? undefined },
        refetchQueries: QUERY_MUTATION_REFETCH,
        awaitRefetchQueries: true,
      });
      const archiveResult = data?.finpubQueryDelete;
      if (errors?.length || !archiveResult?.success) {
        toast.error(
          archiveResult?.message || errors?.[0]?.message || "Failed to archive query",
        );
        return;
      }
      setIsArchiveModalOpen(false);
      toast.success("Query has been archived");
      await onQueryMutated?.();
      onArchive?.();
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : "Failed to archive query. Please try again.";
      toast.error(message);
    }
  }, [queryId, arenaId, deleteFinpubQuery, onArchive, onQueryMutated]);

  const handleProceedToUpdate = useCallback(() => {
    setIsInUseWarningOpen(false);
    onRequestEdit?.();
  }, [onRequestEdit]);

  const handleCloseArchiveModal = useCallback(() => {
    setIsArchiveModalOpen(false);
  }, []);

  const handleCloseCannotArchiveModal = useCallback(() => {
    setIsCannotArchiveModalOpen(false);
  }, []);

  const handleRestoreQuery = useCallback(async () => {
    if (queryId === null) return;
    try {
      const { data, errors } = await restoreFinpubQuery({
        variables: { queryId, arenaId: arenaId ?? undefined },
        refetchQueries: QUERY_MUTATION_REFETCH,
        awaitRefetchQueries: true,
      });
      const restoreResult = data?.finpubQueryRestore;
      if (errors?.length || !restoreResult?.success) {
        toast.error(
          restoreResult?.message || errors?.[0]?.message || "Failed to restore query",
        );
        return;
      }
      toast.success("Query has been restored");
      await onQueryMutated?.();
      onRestore?.();
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : "Failed to restore query. Please try again.";
      toast.error(message);
    }
  }, [queryId, arenaId, restoreFinpubQuery, onRestore, onQueryMutated]);

  const handleCloseInUseWarning = useCallback(() => {
    setIsInUseWarningOpen(false);
  }, []);

  // Mirrors the template editor's exit flow: confirm before tossing edits.
  // Always confirms in edit mode — even on a brand-new untouched query —
  // since the deferred-create flow gives users a real "step away" affordance
  // and we want a single predictable copy. View mode exits silently.
  const handleClickExit = useCallback(() => {
    if (isViewMode) {
      onExit();
      return;
    }
    setIsDiscardModalOpen(true);
  }, [isViewMode, onExit]);

  const handleConfirmDiscard = useCallback(() => {
    setIsDiscardModalOpen(false);
    setEditingFootnote(null);
    if (!isNewQuery && onCancelEdit) {
      // The takeover stays mounted across /edit → view, so explicitly
      // drop the local edit buffers and re-seed `fieldRows` from the
      // cached server fragment. Without this the read-only view (and
      // any later /edit re-entry) would still surface the discarded
      // edits, and a Save from that state could ship them to the BE.
      setPendingSettings(null);
      setPendingFieldEdits(new Map());
      setHasUnsavedFieldEdits(false);
      setHasUnsavedFootnoteEdits(false);
      if (finpubQuery && finpubQuery.id === queryId) {
        setFieldRows(toQueryFieldRows(finpubQuery.fields, rollupLevelOptions));
        setFootnoteRows(toQueryFootnoteRows(finpubQuery.footnotes));
      } else {
        // Fragment for this id isn't in cache yet — drop local rows and
        // let the seeding effect re-seed when the refetch lands.
        setFieldRows([]);
        setFootnoteRows([]);
        seededQueryIdRef.current = null;
      }
      onCancelEdit();
    } else {
      onExit();
    }
  }, [isNewQuery, onCancelEdit, onExit, finpubQuery, queryId, rollupLevelOptions]);

  const handleKeepEditing = useCallback(() => {
    setIsDiscardModalOpen(false);
  }, []);

  const handleSaveAndExit = useCallback(async () => {
    const saved = await handleSave();
    if (saved) {
      setIsDiscardModalOpen(false);
    }
  }, [handleSave]);

  // Browser-level guard for tab close / refresh / external nav. Covers the
  // case the in-app Cancel modal can't (the takeover is unmounted on route
  // change before our handler runs). Only interrupts when there are actual
  // unsaved changes so a pristine edit-mode visit doesn't trigger the prompt.
  useBeforeUnloadPrompt(
    !isViewMode &&
      (pendingSettings !== null ||
        hasUnsavedFieldEdits ||
        hasUnsavedFootnoteEdits ||
        pendingFieldEdits.size > 0),
  );

  const handleTabClick = useCallback((id: QueryDetailTab) => {
    setActiveTab(id);
  }, []);

  const queryFormatValues = useMemo<DataFormatValues>(
    () => ({
      decimalPlaces: settingsInitialValues.decimalPlaces,
      negativeNumbers: settingsInitialValues.negativeNumbers,
      numberScale: settingsInitialValues.numberScale,
      thousandsSeparator: settingsInitialValues.thousandsSeparator,
      showCurrencySymbol: settingsInitialValues.showCurrencySymbol,
      currencySymbolPosition: settingsInitialValues.currencySymbolPosition,
      zeroDisplay: settingsInitialValues.zeroDisplay,
      nullDisplay: settingsInitialValues.nullDisplay,
      dateFormatPattern: settingsInitialValues.dateFormatPattern,
    }),
    [settingsInitialValues],
  );

  const isQueryArchived = finpubQuery?.isActive === false;
  const canPreviewQuery = isViewMode && arenaId !== null && queryId !== null;
  // Action visibility is recomputed per archive state so the menu doesn't
  // surface a Restore item for archived queries when the parent never
  // wired up `onRestore` (and conversely doesn't surface Preview /
  // Archive items the archive state wouldn't accept). `showActionsMenu`
  // only opens the dropdown when at least one item is renderable —
  // otherwise the user gets an empty popover.
  const showPreviewAction = !isQueryArchived && !isDataPackageArchived && canPreviewQuery;
  const showArchiveAction =
    isViewMode && !isQueryArchived && !isDataPackageArchived && Boolean(onArchive);
  const showActionsMenu = showPreviewAction || showArchiveAction;
  const showRestoreFooter =
    isViewMode && isQueryArchived && !isDataPackageArchived && Boolean(onRestore);
  const showUpdateButton =
    isViewMode && Boolean(onRequestEdit) && !isQueryArchived && !isDataPackageArchived;
  // Usage-gated actions (archive and update) stay disabled until template
  // usage resolves: archiving must not proceed on unknown usage, and editing
  // must not skip the in-use warning just because the templates haven't
  // loaded yet.
  const isUsageResolved = usageResolution === "resolved";
  const usageUnresolvedHelp =
    usageResolution === "loading"
      ? "Checking which templates use this query…"
      : usageResolution === "error"
        ? "Template usage failed to load, so this query cannot be archived. Reopen this menu to retry."
        : undefined;
  const rightActionContent =
    showActionsMenu || showUpdateButton ? (
      <Flex alignItems="center" gap={SPACING.SMALL}>
        {showActionsMenu && (
          <DropdownButton
            icon="ellipsis-h"
            align="right"
            variant="neutral"
            onToggle={handleActionsMenuToggle}
            data-testid="query-detail-actions-btn"
          >
            {showPreviewAction && (
              <DropdownButton.Action
                disabled={isPreviewingQuery}
                onSelect={handlePreviewQuery}
                data-testid="preview-query-action"
              >
                Preview query
              </DropdownButton.Action>
            )}
            {showArchiveAction && (
              <DropdownButton.Action
                color={Colors.Danger600}
                onSelect={handleClickArchive}
                disabled={!isUsageResolved}
                helpContent={usageUnresolvedHelp}
                data-cy="archive-query-action"
              >
                Archive query
              </DropdownButton.Action>
            )}
          </DropdownButton>
        )}
        {showUpdateButton && (
          <Button
            variant="primary"
            onClick={handleStartEdit}
            disabled={!isUsageResolved}
            data-testid="query-detail-update-btn"
          >
            Update query
          </Button>
        )}
      </Flex>
    ) : undefined;

  let headerTitle: string;
  let headerSubtitle: string | undefined;
  if (isViewMode) {
    headerTitle = queryName;
    headerSubtitle = undefined;
  } else if (isNewQuery) {
    headerTitle = "New query";
    headerSubtitle = queryName;
  } else {
    headerTitle = "Update query";
    headerSubtitle = queryName;
  }

  return (
    <FullscreenTakeover>
      <FullscreenTakeover.Header
        title={headerTitle}
        subtitle={headerSubtitle}
        leftActionContent={
          <Button
            variant="muted"
            icon="xmark"
            onClick={handleClickExit}
            data-testid="query-detail-exit-btn"
          >
            Exit
          </Button>
        }
        rightActionContent={rightActionContent}
      />
      <FullscreenTakeover.Body className={styles.takeoverBody} overflowY="hidden">
        <div className={styles.card} data-testid="query-detail-card">
          <Flex
            flexDirection="row"
            alignItems="flex-start"
            justifyContent="space-between"
            className={styles.cardHeader}
          >
            <Flex flexDirection="column" gap={SPACING.X_SMALL}>
              <Text variant="h4">Fields</Text>
              <Flex
                flexDirection="row"
                alignItems="center"
                gap={SPACING.X_SMALL}
                className={styles.cardSubtitle}
              >
                <Text variant="secondary-md">
                  Add and configure all the fields you&apos;ll need to create your desired
                  report template.
                </Text>
              </Flex>
            </Flex>
            <Button
              variant="neutral"
              icon="cog"
              iconPosition="left"
              onClick={handleOpenSettings}
              data-testid="query-settings-btn"
            >
              Settings
            </Button>
          </Flex>

          <Tabs className={styles.queryDetailTabs} hasPaddingInline={false}>
            {TAB_CONFIG.map(({ id, label }) => (
              <Tab
                key={id}
                data-testid={`query-tab-${id}`}
                active={activeTab === id}
                text={label}
                onClick={() => handleTabClick(id)}
              />
            ))}
          </Tabs>

          {activeTab === "data-fields" && (
            <>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                gap={SPACING.MEDIUM}
                className={styles.toolbar}
              >
                <TextInput
                  value={searchValue}
                  onChange={handleSearchChange}
                  placeholder="Search name"
                  isClearable
                  onClearValue={handleClearSearch}
                  data-testid="fields-search"
                  disabled={fields.length === 0}
                />
                {!isViewMode && (
                  <Button
                    variant="neutral"
                    icon="plus"
                    iconPosition="left"
                    onClick={handleOpenAddField}
                    data-testid="insert-fields-btn"
                  >
                    Add fields
                  </Button>
                )}
              </Flex>
              <div className={styles.gridContainer}>
                <FieldsGrid
                  fields={fields}
                  searchValue={searchValue}
                  isViewMode={isViewMode}
                  onConfigureField={isViewMode ? undefined : handleConfigureField}
                  onDeleteField={isViewMode ? undefined : handleDeleteField}
                />
              </div>
            </>
          )}

          {activeTab === "footnotes" && (
            <>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                gap={SPACING.MEDIUM}
                className={styles.toolbar}
              >
                <TextInput
                  value={footnoteSearchValue}
                  onChange={handleFootnoteSearchChange}
                  placeholder="Search footnotes"
                  isClearable
                  onClearValue={handleClearFootnoteSearch}
                  data-testid="footnotes-search"
                  disabled={footnotes.length === 0}
                />
                {!isViewMode &&
                  (footnotes.length >= MAX_FOOTNOTES ? (
                    // A native disabled button emits no hover events, so the
                    // tooltip trigger has to be a wrapping element.
                    <HelpIndicator
                      id="footnote-limit"
                      placement="top"
                      content={`Maximum ${MAX_FOOTNOTES} footnotes`}
                    >
                      <span className={styles.addFootnoteButtonWrapper}>
                        <Button
                          variant="neutral"
                          icon="plus"
                          iconPosition="left"
                          disabled
                          data-testid="add-footnote-btn"
                        >
                          New footnote
                        </Button>
                      </span>
                    </HelpIndicator>
                  ) : (
                    <Button
                      variant="neutral"
                      icon="plus"
                      iconPosition="left"
                      onClick={handleOpenAddFootnote}
                      data-testid="add-footnote-btn"
                    >
                      New footnote
                    </Button>
                  ))}
              </Flex>
              <div className={styles.gridContainer}>
                <FootnotesGrid
                  footnotes={footnotes}
                  searchValue={footnoteSearchValue}
                  isViewMode={isViewMode}
                  onEditFootnote={isViewMode ? undefined : handleEditFootnote}
                  onDeleteFootnote={isViewMode ? undefined : handleDeleteFootnote}
                  onReorderFootnotes={isViewMode ? undefined : handleReorderFootnotes}
                />
              </div>
            </>
          )}
          {activeTab === "custom-message" && <ComingSoon title="Custom messages coming soon" />}
        </div>
      </FullscreenTakeover.Body>
      {!isViewMode && (
        <FullscreenTakeover.Footer>
          <div className={styles.footer}>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!canSaveQuery}
              data-testid="save-query-btn"
            >
              Save query
            </Button>
          </div>
        </FullscreenTakeover.Footer>
      )}
      {showRestoreFooter && (
        <FullscreenTakeover.Footer>
          <div className={styles.footer}>
            <Button
              variant="neutral"
              onClick={() => void handleRestoreQuery()}
              isLoading={isRestoring}
              data-testid="restore-query-btn"
            >
              Restore query
            </Button>
          </div>
        </FullscreenTakeover.Footer>
      )}

      <FieldSettingsPanel
        isOpen={selectedFieldId !== null}
        onClose={handleCloseFieldSettings}
        fieldRow={selectedFieldRow}
        onSave={handleSaveFieldSettings}
        queryFormatValues={queryFormatValues}
        isReadOnly={isViewMode}
        arenaId={arenaId}
        rollupLevelOptions={rollupLevelOptions}
        attributeLeaves={attributeLeaves}
        typeHierarchy={typeHierarchy}
        formulas={formulas}
        pendingFormulaComponents={
          selectedFieldId !== null
            ? pendingFieldEdits.get(selectedFieldId)?.formulaComponents
            : undefined
        }
      />
      <QuerySettingsPanel
        isOpen={isSettingsPanelOpen}
        onClose={handleCloseSettings}
        initialValues={settingsInitialValues}
        nameError={queryNameError}
        onSave={handleSaveSettings}
        isReadOnly={isViewMode}
        arenaId={arenaId}
        attributeLeaves={attributeLeaves}
        typeHierarchy={typeHierarchy}
      />
      <FootnoteSettingsPanel
        isOpen={editingFootnote !== null}
        onClose={handleCloseFootnotePanel}
        footnoteRaw={editingFootnote?.raw ?? null}
        onSave={handleSaveFootnote}
        isReadOnly={isViewMode}
        arenaId={arenaId}
        attributeLeaves={attributeLeaves}
        fieldOptions={footnoteFieldOptions}
        customFieldOptionGroups={customFieldOptionGroups}
        isLoadingCustomFields={isLoadingCustomFields}
        hasCustomFieldsError={customFieldsError != null}
        queryFootnoteSymbol={settingsInitialValues.footnoteSymbol}
        takenNames={takenFootnoteNames}
        takenCustomFieldIds={takenFootnoteCustomFieldIds}
      />
      <AddFieldPickerSlideout
        isOpen={isAddFieldOpen}
        onClose={handleCloseAddField}
        arenaId={arenaId}
        typeHierarchy={typeHierarchy}
        formulas={formulas}
        nextSortOrder={fieldRows.length}
        onAddField={handleAddField}
        rollupLevelOptions={rollupLevelOptions}
      />
      <ArchiveQueryModal
        isOpen={isArchiveModalOpen}
        onClose={handleCloseArchiveModal}
        onConfirm={handleConfirmArchive}
        isArchiving={isArchiving}
      />
      <QueryCannotBeArchivedModal
        isOpen={isCannotArchiveModalOpen}
        onClose={handleCloseCannotArchiveModal}
      />
      <QueryInUseWarningModal
        isOpen={isInUseWarningOpen}
        onClose={handleCloseInUseWarning}
        onProceed={handleProceedToUpdate}
        templates={usageTemplates}
      />
      <QueryPreviewModal
        isOpen={isPreviewModalOpen}
        isLoading={isPreviewingQuery}
        rows={previewRows}
        requiresGlTransactions={requiresPreviewGlTransactions}
        entities={previewEntities}
        isLoadingEntities={isLoadingPreviewEntities}
        selectedEntityIds={selectedPreviewEntityIds}
        onEntitiesChange={handlePreviewEntitiesChange}
        transactions={previewTransactions}
        isLoadingTransactions={isLoadingPreviewTransactions}
        selectedTransactionIds={selectedPreviewTransactionIds}
        onTransactionsChange={handlePreviewTransactionsChange}
        onExecute={handleExecutePreview}
        onClose={handleClosePreviewModal}
      />
      <QueryUnsavedChangesModal
        show={isDiscardModalOpen}
        isSaving={isSaving}
        onKeepEditing={handleKeepEditing}
        onDiscard={handleConfirmDiscard}
        onSave={handleSaveAndExit}
      />
    </FullscreenTakeover>
  );
};
